/**
 * FikoNesting — 2D Levha Yerleşim Optimizasyonu (Nesting)
 * MaxRects (Best Short Side Fit) + rotasyon + çoklu levha. Fire/verim hesabı.
 * Sac/panel/strafor parçalarını levhaya en az fire ile yerleştirir.
 */

export interface NestParca { ad: string; en: number; boy: number; adet: number; donebilir?: boolean; }
export interface NestLevha { en: number; boy: number; bosluk?: number; }   // bosluk = kerf/parçalar arası pay (mm)
export interface NestYerlesim { ad: string; x: number; y: number; en: number; boy: number; dondu: boolean; levha: number; }
export interface NestSonuc {
  yerlesimler: NestYerlesim[];
  levhaSayisi: number;
  yerlesen: number; yerlesemeyen: number;
  toplamParcaAlan: number;   // mm²
  toplamLevhaAlan: number;   // mm²
  verimYuzde: number;        // parça/levha
  fireYuzde: number;
  yerlesemeyenler: string[];
}

interface Rect { x: number; y: number; w: number; h: number; }

/** Bir levhanın boş alan yönetimi (MaxRects). */
class MaxRectsBin {
  free: Rect[];
  constructor(public W: number, public H: number) { this.free = [{ x: 0, y: 0, w: W, h: H }]; }

  /** En iyi boş dikdörtgene yerleştir (BSSF). Sığmazsa null. */
  yerlestir(w: number, h: number, donebilir: boolean): { x: number; y: number; w: number; h: number; dondu: boolean } | null {
    let best: any = null, bestKisa = Infinity, bestUzun = Infinity;
    for (const f of this.free) {
      // normal yön
      if (w <= f.w && h <= f.h) {
        const kisa = Math.min(f.w - w, f.h - h), uzun = Math.max(f.w - w, f.h - h);
        if (kisa < bestKisa || (kisa === bestKisa && uzun < bestUzun)) { best = { x: f.x, y: f.y, w, h, dondu: false }; bestKisa = kisa; bestUzun = uzun; }
      }
      // döndürülmüş
      if (donebilir && h <= f.w && w <= f.h) {
        const kisa = Math.min(f.w - h, f.h - w), uzun = Math.max(f.w - h, f.h - w);
        if (kisa < bestKisa || (kisa === bestKisa && uzun < bestUzun)) { best = { x: f.x, y: f.y, w: h, h: w, dondu: true }; bestKisa = kisa; bestUzun = uzun; }
      }
    }
    if (!best) return null;
    this.boluVeBuda({ x: best.x, y: best.y, w: best.w, h: best.h });
    return best;
  }

  private boluVeBuda(yer: Rect): void {
    const yeni: Rect[] = [];
    for (const f of this.free) {
      if (!this.kesisir(f, yer)) { yeni.push(f); continue; }
      // f'i yer'in dışında kalan 4 parçaya böl
      if (yer.x > f.x) yeni.push({ x: f.x, y: f.y, w: yer.x - f.x, h: f.h });
      if (yer.x + yer.w < f.x + f.w) yeni.push({ x: yer.x + yer.w, y: f.y, w: f.x + f.w - (yer.x + yer.w), h: f.h });
      if (yer.y > f.y) yeni.push({ x: f.x, y: f.y, w: f.w, h: yer.y - f.y });
      if (yer.y + yer.h < f.y + f.h) yeni.push({ x: f.x, y: yer.y + yer.h, w: f.w, h: f.y + f.h - (yer.y + yer.h) });
    }
    // kapsanan (başka birinin içinde kalan) boş dikdörtgenleri ele
    this.free = yeni.filter((a, i) => a.w > 0.01 && a.h > 0.01 && !yeni.some((b, j) => i !== j && this.kapsar(b, a)));
  }
  private kesisir(a: Rect, b: Rect): boolean { return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y; }
  private kapsar(a: Rect, b: Rect): boolean { return a.x <= b.x && a.y <= b.y && a.x + a.w >= b.x + b.w && a.y + a.h >= b.y + b.h; }
}

/** Parçaları levha(lar)a yerleştir → yerleşim + fire/verim. */
export function nestingHesapla(parcalar: NestParca[], levha: NestLevha): NestSonuc {
  const bosluk = Math.max(0, levha.bosluk || 0);
  // adet'leri aç + boşluk payı ekle (kenarlara yarım pay → +bosluk)
  const items: { ad: string; w: number; h: number; donebilir: boolean }[] = [];
  for (const p of parcalar) {
    const n = Math.max(0, Math.floor(p.adet || 0));
    for (let i = 0; i < n; i++) items.push({ ad: p.ad, w: p.en + bosluk, h: p.boy + bosluk, donebilir: p.donebilir !== false });
  }
  items.sort((a, b) => b.w * b.h - a.w * a.h);   // büyükten küçüğe (FFD)

  const yerlesimler: NestYerlesim[] = [];
  const yerlesemeyenler: string[] = [];
  const binler: MaxRectsBin[] = [];
  let toplamParcaAlan = 0;

  for (const it of items) {
    let kondu = false;
    for (let b = 0; b < binler.length; b++) {
      const r = binler[b].yerlestir(it.w, it.h, it.donebilir);
      if (r) { yerlesimler.push({ ad: it.ad, x: r.x, y: r.y, en: r.w - bosluk, boy: r.h - bosluk, dondu: r.dondu, levha: b }); toplamParcaAlan += (it.w - bosluk) * (it.h - bosluk); kondu = true; break; }
    }
    if (!kondu) {
      // yeni levha aç (parça levhaya tek başına sığıyorsa)
      if (it.w <= levha.en + bosluk && it.h <= levha.boy + bosluk) {
        const bin = new MaxRectsBin(levha.en, levha.boy); binler.push(bin);
        const r = bin.yerlestir(it.w, it.h, it.donebilir);
        if (r) { yerlesimler.push({ ad: it.ad, x: r.x, y: r.y, en: r.w - bosluk, boy: r.h - bosluk, dondu: r.dondu, levha: binler.length - 1 }); toplamParcaAlan += (it.w - bosluk) * (it.h - bosluk); kondu = true; }
      }
      if (!kondu) yerlesemeyenler.push(`${it.ad} (${(it.w - bosluk).toFixed(0)}×${(it.h - bosluk).toFixed(0)})`);
    }
  }

  const map: NestYerlesim[] = yerlesimler;   // map levha index → yerleşim (görsel için levha alanı)
  const levhaSayisi = binler.length;
  const toplamLevhaAlan = levhaSayisi * levha.en * levha.boy;
  const verimYuzde = toplamLevhaAlan > 0 ? (toplamParcaAlan / toplamLevhaAlan) * 100 : 0;
  return {
    yerlesimler: map,
    levhaSayisi,
    yerlesen: yerlesimler.length, yerlesemeyen: yerlesemeyenler.length,
    toplamParcaAlan, toplamLevhaAlan,
    verimYuzde, fireYuzde: toplamLevhaAlan > 0 ? 100 - verimYuzde : 0,
    yerlesemeyenler,
  };
}
